import React, { useState } from "react";

import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TrashIcon, checkIcon } from "@excalidraw/excalidraw/components/icons";

import "./FolderSection.scss";

import type { FolderMetadata } from "../../types/customFileLibrary";

interface FolderSectionProps {
  folder: FolderMetadata;
  isCollapsed: boolean;
  onToggleCollapse: (folderId: string) => void;
  onRename: (folderId: string, newName: string) => void;
  onDelete: (folderId: string) => void;
  children?: React.ReactNode;
  showActions?: boolean; // Optional prop to hide rename/delete actions
  fileCount?: number; // Number of files in this folder
}

const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
      transition: "transform 0.2s ease",
    }}
  >
    <path
      d="M6 4L10 8L6 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FolderSection: React.FC<FolderSectionProps> = ({
  folder,
  isCollapsed,
  onToggleCollapse,
  onRename,
  onDelete,
  children,
  showActions = true,
  fileCount = 0,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const handleDoubleClick = () => {
    setIsEditing(true);
    setEditName(folder.name);
  };

  const handleRename = () => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== folder.name) {
      onRename(folder.id, trimmedName);
    } else {
      setEditName(folder.name);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleRename();
    } else if (e.key === "Escape") {
      setEditName(folder.name);
      setIsEditing(false);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirmDelete) {
      onDelete(folder.id);
      setShowConfirmDelete(false);
    } else {
      setShowConfirmDelete(true);
      setTimeout(() => setShowConfirmDelete(false), 3000);
    }
  };

  return (
    <div className="FolderSection">
      <div className="FolderSection__header">
        <button
          className="FolderSection__toggle"
          onClick={() => onToggleCollapse(folder.id)}
          aria-label={isCollapsed ? "Expand folder" : "Collapse folder"}
        >
          <ChevronIcon isOpen={!isCollapsed} />
        </button>

        {isEditing && showActions ? (
          <input
            type="text"
            className="FolderSection__name-input"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={handleKeyDown}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="FolderSection__name-wrapper">
            <h3
              className="FolderSection__name"
              onDoubleClick={showActions ? handleDoubleClick : undefined}
              title={showActions ? "Double-click to rename" : undefined}
            >
              {folder.name}
            </h3>
            <span className="FolderSection__file-count">({fileCount})</span>
          </div>
        )}

        {showActions && (
          <div className="FolderSection__actions">
            <FilledButton
              size="medium"
              variant={showConfirmDelete ? "filled" : "icon"}
              color="danger"
              onClick={handleDelete}
              label={showConfirmDelete ? "Confirm delete?" : "Delete folder"}
              aria-label={
                showConfirmDelete ? "Confirm delete" : "Delete folder"
              }
              icon={showConfirmDelete ? checkIcon : TrashIcon}
            />
          </div>
        )}
      </div>

      {!isCollapsed && (
        <>
          <div className="FolderSection__divider" />
          <div className="FolderSection__content">{children}</div>
        </>
      )}
    </div>
  );
};
